import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import AdminMessageAttachments from '../components/admin/AdminMessageAttachments';
import {
  fetchAdminSupportInbox,
  fetchAdminSupportMessages,
  resolveAdminSupportTripClose,
  searchAdminSupportTrips,
  sendAdminSupportMessage,
} from '../services/api/admin';
import type { AdminReviewMessage, AdminSupportConversationSummary, AdminTripListItem } from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';

function getMessageCreatedAt(message: AdminReviewMessage): string | null {
  return message.createdAtIso || message.createdAt || null;
}

function getLastMessageCreatedAt(conversation: AdminSupportConversationSummary): string | null {
  const last = conversation.lastMessage;
  if (!last) return null;
  if (last.createdAtIso) return last.createdAtIso;
  if (last.createdAt) return last.createdAt;
  if (typeof last.createdAtEpochSec === 'number') {
    return new Date(last.createdAtEpochSec * 1000).toISOString();
  }
  return null;
}

function getPreview(conversation: AdminSupportConversationSummary): string {
  const content = conversation.lastMessage?.content?.trim();
  if (content) return content;

  const attachmentCount = conversation.lastMessage?.attachmentCount ?? 0;
  if (attachmentCount > 1) return `${attachmentCount} attachments`;
  if (attachmentCount === 1) return 'Attachment';
  return 'No messages yet';
}

function getConversationTitle(conversation: AdminSupportConversationSummary): string {
  const title = conversation.title?.trim();
  if (title) return title;
  return conversation.bookingId || conversation.conversationId || 'Support thread';
}

function conversationKey(conversation: AdminSupportConversationSummary): string {
  return conversation.bookingId || conversation.conversationId;
}

function isWhatsAppConversation(conversation?: AdminSupportConversationSummary | null): boolean {
  if (!conversation) return false;
  return Boolean(
    conversation.bookingId?.startsWith('SUPPORT:WHATSAPP:')
      || getConversationTitle(conversation).toLowerCase().includes('whatsapp'),
  );
}

function isUnread(conversation: AdminSupportConversationSummary): boolean {
  return Boolean((conversation.unreadCount ?? 0) > 0);
}

function normalizeMessageContent(content?: string | null): string {
  const value = content?.trim();
  return value ? value : 'No text content';
}

const SUPPORT_CLOSE_STATUSES = [
  'ON_ROUTE_TO_PICKUP',
  'LOADING',
  'PICKED_UP',
  'ENROUTE',
  'UNLOADING',
  'DELIVERED',
];

function formatStatusLabel(status: string | null | undefined): string {
  return (status || 'UNKNOWN').replaceAll('_', ' ');
}

function tripSearchLabel(trip: AdminTripListItem): string {
  return trip.representativeBookingId || trip.tripId || 'Trip';
}

const AdminSupportPage: React.FC = () => {
  const [conversations, setConversations] = useState<AdminSupportConversationSummary[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<AdminReviewMessage[]>([]);
  const [inboxSearch, setInboxSearch] = useState('');
  const [inboxFilter, setInboxFilter] = useState<'all' | 'unread' | 'whatsapp' | 'app'>('all');
  const [tripQuery, setTripQuery] = useState('');
  const [tripStatusFilter, setTripStatusFilter] = useState('');
  const [tripMatches, setTripMatches] = useState<AdminTripListItem[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<AdminTripListItem | null>(null);
  const [tripSearchLoading, setTripSearchLoading] = useState(false);
  const [tripResolving, setTripResolving] = useState(false);
  const [tripResolutionReason, setTripResolutionReason] = useState('');
  const [tripResolutionMessage, setTripResolutionMessage] = useState('');
  const [tripResolutionError, setTripResolutionError] = useState('');
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [messageSuccess, setMessageSuccess] = useState('');
  const messageListRef = useRef<HTMLDivElement | null>(null);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversationKey(conversation) === selectedKey) ?? null,
    [conversations, selectedKey],
  );

  const sortedConversations = useMemo(() => {
    const term = inboxSearch.trim().toLowerCase();
    return conversations.filter((conversation) => {
      if (inboxFilter === 'unread' && !isUnread(conversation)) return false;
      if (inboxFilter === 'whatsapp' && !isWhatsAppConversation(conversation)) return false;
      if (inboxFilter === 'app' && isWhatsAppConversation(conversation)) return false;
      if (!term) return true;
      return [
        getConversationTitle(conversation),
        conversation.bookingId,
        conversation.conversationId,
        getPreview(conversation),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    }).sort((a, b) => {
      const aTime = getLastMessageCreatedAt(a);
      const bTime = getLastMessageCreatedAt(b);
      const aMs = aTime ? new Date(aTime).getTime() : 0;
      const bMs = bTime ? new Date(bTime).getTime() : 0;
      return bMs - aMs;
    });
  }, [conversations, inboxFilter, inboxSearch]);

  const inboxStats = useMemo(() => {
    const unread = conversations.filter(isUnread).length;
    const whatsapp = conversations.filter(isWhatsAppConversation).length;
    return {
      total: conversations.length,
      unread,
      whatsapp,
      app: Math.max(0, conversations.length - whatsapp),
    };
  }, [conversations]);

  const scrollMessagesToBottom = useCallback(() => {
    window.requestAnimationFrame(() => {
      const element = messageListRef.current;
      if (!element) return;
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  const loadInbox = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoadingInbox(true);
    }
    setError('');

    try {
      const response = await fetchAdminSupportInbox({ page: 0, size: 50 });
      setConversations(response.content);
      setSelectedKey((current) => {
        if (current && response.content.some((conversation) => conversationKey(conversation) === current)) {
          return current;
        }
        return response.content[0] ? conversationKey(response.content[0]) : null;
      });
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load support inbox.');
    } finally {
      if (!options?.silent) {
        setLoadingInbox(false);
      }
    }
  }, []);

  const loadMessages = useCallback(async (supportKey: string, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoadingMessages(true);
    }
    setMessageError('');

    try {
      const response = await fetchAdminSupportMessages(supportKey);
      setMessages(response);
      if (!options?.silent) {
        scrollMessagesToBottom();
      }
    } catch (loadError: any) {
      setMessageError(loadError.message || 'Could not load support messages.');
      setMessages([]);
    } finally {
      if (!options?.silent) {
        setLoadingMessages(false);
      }
    }
  }, [scrollMessagesToBottom]);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!selectedKey) {
      setMessages([]);
      return undefined;
    }

    void loadMessages(selectedKey);
    const interval = window.setInterval(() => {
      void loadInbox({ silent: true });
      void loadMessages(selectedKey, { silent: true });
    }, 15000);

    return () => window.clearInterval(interval);
  }, [loadInbox, loadMessages, selectedKey]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollMessagesToBottom();
    }
  }, [messages.length, scrollMessagesToBottom]);

  const handleSend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedKey || sending) return;

    const content = draft.trim();
    if (!content) {
      setMessageError('Enter a reply before sending.');
      return;
    }

    setSending(true);
    setMessageError('');
    setMessageSuccess('');

    try {
      const sent = await sendAdminSupportMessage({ supportKey: selectedKey, content });
      setMessages((current) => [...current, sent]);
      setDraft('');
      setMessageSuccess('Reply sent to the user.');
      void loadInbox({ silent: true });
      scrollMessagesToBottom();
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the support reply.');
    } finally {
      setSending(false);
    }
  };

  const loadSupportTripSearch = useCallback(async (query: string, status: string) => {
    if (!query && !status) {
      setTripResolutionError('Enter a booking ref, phone, email, name, address, or choose a status.');
      return;
    }

    setTripSearchLoading(true);
    setTripResolutionError('');
      setTripResolutionMessage('');

    try {
      const response = await searchAdminSupportTrips({
        ...(query ? { query } : {}),
        ...(status ? { status } : {}),
        page: 0,
        size: 10,
      });
      setTripMatches(response.items);
      setSelectedTrip(response.items[0] ?? null);
      if (response.items.length === 0) {
        setTripResolutionError('No matching trips found. Try the customer or driver phone/email, address, or booking reference.');
      }
    } catch (searchError: any) {
      setTripResolutionError(searchError.message || 'Could not search trips.');
    } finally {
      setTripSearchLoading(false);
    }
  }, []);

  const handleTripSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await loadSupportTripSearch(tripQuery.trim(), tripStatusFilter);
  };

  const handleResolveTripClose = async () => {
    if (!selectedTrip || tripResolving) return;

    const reason = tripResolutionReason.trim();
    if (reason.length < 10) {
      setTripResolutionError('Enter a clear support reason of at least 10 characters.');
      return;
    }

    setTripResolving(true);
    setTripResolutionError('');
    setTripResolutionMessage('');

    try {
      const response = await resolveAdminSupportTripClose({
        tripId: selectedTrip.tripId,
        bookingId: selectedTrip.representativeBookingId,
        reason,
      });
      setTripResolutionMessage(
        `Closed ${response.bookingId || response.tripId} from ${formatStatusLabel(response.previousStatus)} to ${formatStatusLabel(response.status)}.`,
      );
      setTripResolutionReason('');
      await loadSupportTripSearch(tripQuery.trim(), tripStatusFilter);
    } catch (resolveError: any) {
      setTripResolutionError(resolveError.message || 'Could not close this trip.');
    } finally {
      setTripResolving(false);
    }
  };

  return (
    <AdminShell
      badge="Support"
      title="Support Inbox"
      copy="Review support threads from WhatsApp and the app Help menu, then reply from the admin console."
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <section className="admin-panel" style={{ marginBottom: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Resolve stuck trip</h2>
            <p className="admin-panel-copy">Find the booking from the details the driver or customer gives support, then close the selected in-flight trip with an audit reason.</p>
          </div>
        </div>

        {tripResolutionError ? <div className="register-inline-error">{tripResolutionError}</div> : null}
        {tripResolutionMessage ? <div className="register-inline-success">{tripResolutionMessage}</div> : null}

        <form className="admin-toolbar admin-toolbar-trips" onSubmit={handleTripSearch}>
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setTripQuery(event.target.value);
              setTripResolutionError('');
              setTripResolutionMessage('');
            }}
            placeholder="Booking ref, trip ID, phone, email, name, driver ID, customer ID, or address"
            value={tripQuery}
          />
          <select
            className="register-input"
            onChange={(event) => setTripStatusFilter(event.target.value)}
            value={tripStatusFilter}
          >
            <option value="">In-flight and recent</option>
            {SUPPORT_CLOSE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </select>
          <button className="register-secondary-button" disabled={tripSearchLoading} type="submit">
            {tripSearchLoading ? 'Searching...' : 'Search'}
          </button>
        </form>

        <div className="admin-list" style={{ marginTop: '14px' }}>
          {tripMatches.map((trip) => {
            const selected = selectedTrip?.tripId === trip.tripId;
            return (
              <button
                className={`admin-list-card admin-support-trip-card${selected ? ' admin-support-thread-card-active' : ''}`}
                key={trip.tripId}
                onClick={() => {
                  setSelectedTrip(trip);
                  setTripResolutionError('');
                  setTripResolutionMessage('');
                }}
                type="button"
              >
                <strong>{tripSearchLabel(trip)}</strong>
                <small>{formatStatusLabel(trip.status)} - {formatDateTime(trip.createdAt)}</small>
                <span>{trip.activeDriverName || trip.activeDriverPhone || trip.activeDriverEmail || 'Driver not resolved'}</span>
                <span>{trip.requesterName || trip.requesterPhone || trip.requesterEmail || 'Requester not resolved'}</span>
                <span>{trip.sourceAddress || 'Pickup not available'} to {trip.destinationAddress || 'Dropoff not available'}</span>
              </button>
            );
          })}
          {!tripSearchLoading && tripMatches.length === 0 ? (
            <p className="admin-empty">Search for a trip before closing it through support.</p>
          ) : null}
        </div>

        {selectedTrip ? (
          <div className="admin-support-close-box" style={{ marginTop: '14px' }}>
            <div className="admin-panel-header">
              <div>
                <h3 className="admin-panel-title">Close selected trip</h3>
                <p className="admin-panel-copy">
                  Selected {tripSearchLabel(selectedTrip)} currently {formatStatusLabel(selectedTrip.status)}.
                </p>
              </div>
            </div>
            <label className="register-label" htmlFor="supportTripCloseReason">Support reason</label>
            <textarea
              className="register-input"
              id="supportTripCloseReason"
              onChange={(event) => {
                setTripResolutionReason(event.target.value);
                setTripResolutionError('');
                setTripResolutionMessage('');
              }}
              placeholder="Example: Driver called support; POD confirmed in chat and app is stuck on ENROUTE."
              rows={4}
              value={tripResolutionReason}
            />
            <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
              <button
                className="register-submit register-submit-inline"
                disabled={tripResolving || !tripResolutionReason.trim()}
                onClick={handleResolveTripClose}
                type="button"
              >
                {tripResolving ? 'Closing trip...' : 'Close trip as completed'}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <div className="admin-support-layout">
        <section className="admin-panel admin-support-inbox-panel">
          <div className="admin-support-inbox-head">
            <div>
              <h2 className="admin-panel-title">Inbox</h2>
              <p className="admin-panel-copy">{inboxStats.unread} need admin action.</p>
            </div>
            <button
              className="register-secondary-button admin-support-refresh-button"
              disabled={loadingInbox}
              onClick={() => void loadInbox()}
              type="button"
            >
              {loadingInbox ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="admin-support-search-row">
            <input
              className="register-input"
              onChange={(event) => setInboxSearch(event.target.value)}
              placeholder="Search name, phone, message, thread"
              value={inboxSearch}
            />
          </div>

          <div className="admin-support-filter-row" aria-label="Support inbox filters">
            {[
              { key: 'all', label: 'All', count: inboxStats.total },
              { key: 'unread', label: 'Unread', count: inboxStats.unread },
              { key: 'whatsapp', label: 'WhatsApp', count: inboxStats.whatsapp },
              { key: 'app', label: 'App', count: inboxStats.app },
            ].map((filter) => (
              <button
                className={`admin-support-filter${inboxFilter === filter.key ? ' admin-support-filter-active' : ''}`}
                key={filter.key}
                onClick={() => setInboxFilter(filter.key as typeof inboxFilter)}
                type="button"
              >
                {filter.label}
                <span>{filter.count}</span>
              </button>
            ))}
          </div>

          <div className="admin-support-thread-list">
            {sortedConversations.map((conversation) => {
              const key = conversationKey(conversation);
              const selected = key === selectedKey;
              const unread = isUnread(conversation);
              const whatsapp = isWhatsAppConversation(conversation);
              return (
                <button
                  className={`admin-support-thread-card${selected ? ' admin-support-thread-card-active' : ''}${unread ? ' admin-support-thread-card-unread' : ''}`}
                  key={key}
                  onClick={() => {
                    setSelectedKey(key);
                    setMessageSuccess('');
                  }}
                  type="button"
                >
                  <span className="admin-support-thread-topline">
                    <span className="admin-support-thread-title">{getConversationTitle(conversation)}</span>
                    {unread ? <span className="admin-support-unread-dot" aria-label="Unread message" /> : null}
                  </span>
                  <span className="admin-support-thread-preview">{getPreview(conversation)}</span>
                  <span className="admin-support-thread-meta">
                    <span className={`admin-support-channel-pill${whatsapp ? ' admin-support-channel-whatsapp' : ''}`}>
                      {whatsapp ? 'WhatsApp' : 'App'}
                    </span>
                    <span>{formatDateTime(getLastMessageCreatedAt(conversation))}</span>
                    {unread ? <strong>{conversation.unreadCount} unread</strong> : null}
                  </span>
                </button>
              );
            })}

            {!loadingInbox && sortedConversations.length === 0 ? (
              <p className="admin-empty">No support threads match this view.</p>
            ) : null}
            {loadingInbox && sortedConversations.length === 0 ? (
              <p className="admin-empty">Loading support inbox...</p>
            ) : null}
          </div>
        </section>

        <section className="admin-panel admin-support-thread-panel">
          <div className="admin-support-thread-head">
            <div>
              <h2 className="admin-panel-title">
                {selectedConversation ? getConversationTitle(selectedConversation) : 'Select a thread'}
              </h2>
              <p className="admin-panel-copy">
                {selectedConversation?.bookingId
                  ? `Thread key: ${selectedConversation.bookingId}`
                  : 'Choose a support thread from the inbox.'}
              </p>
            </div>
            {selectedConversation ? (
              <span className={`admin-support-channel-pill${isWhatsAppConversation(selectedConversation) ? ' admin-support-channel-whatsapp' : ''}`}>
                {isWhatsAppConversation(selectedConversation) ? 'WhatsApp' : 'App'}
              </span>
            ) : null}
          </div>

          {messageError ? <div className="register-inline-error">{messageError}</div> : null}
          {messageSuccess ? <div className="register-inline-success">{messageSuccess}</div> : null}

          <div className="admin-message-list admin-support-message-list" ref={messageListRef}>
            {messages.map((message) => (
              <article
                className={`admin-message-item${message.senderType?.toUpperCase() === 'ADMIN' ? ' admin-message-item-admin' : ''}`}
                key={message.id}
              >
                <div className="admin-message-meta">
                  {message.senderName || message.senderType || 'Message'} - {formatDateTime(getMessageCreatedAt(message))}
                </div>
                <p className="admin-message-content">{normalizeMessageContent(message.content)}</p>
                <AdminMessageAttachments attachments={message.attachments} />
              </article>
            ))}

            {!selectedKey ? <p className="admin-empty">Select a thread to view messages.</p> : null}
            {selectedKey && loadingMessages ? <p className="admin-empty">Loading messages...</p> : null}
            {selectedKey && !loadingMessages && messages.length === 0 ? (
              <p className="admin-empty">No messages in this support thread yet.</p>
            ) : null}
          </div>

          <form className="register-form admin-support-reply-form" onSubmit={handleSend}>
            <label className="register-label" htmlFor="adminSupportReply">Reply to user</label>
            <textarea
              className="register-input"
              disabled={!selectedKey || sending}
              id="adminSupportReply"
              onChange={(event) => {
                setDraft(event.target.value);
                setMessageError('');
                setMessageSuccess('');
              }}
              placeholder="Type a clear support response."
              rows={5}
              value={draft}
            />
            <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
              <button
                className="register-submit register-submit-inline"
                disabled={!selectedKey || sending}
                type="submit"
              >
                {sending ? 'Sending...' : 'Send reply'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </AdminShell>
  );
};

export default AdminSupportPage;
